class RetailerMap extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.canvas = this.querySelector('[data-retailer-map-canvas]');
    this.message = this.querySelector('[data-retailer-map-message]');
    this.cards = Array.from(this.querySelectorAll('[data-retailer-card]'));
    this.list = this.querySelector('.retailer-results__list');
    this.resultsHeading = this.closest('.retailer-results')?.querySelector('[data-retailer-results-heading]') || null;
    this.markers = new Map();
    this.activeCard = this.cards.find((card) => card.classList.contains('is-active')) || this.cards[0] || null;
    this.persistentInfoCard = null;
    this.filterQuery = '';

    this.updateResultsHeading('', this.cards.length);

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('a')) return;
      const card = target.closest('[data-retailer-card]');
      if (card) this.selectRetailer(card, { openInfo: true, pan: true });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('a')) return;
      const card = target.closest('[data-retailer-card]');
      if (!card) return;
      event.preventDefault();
      this.selectRetailer(card, { openInfo: true, pan: true });
    });

    this.updateVisitorDistances();
    this.initializeMap();
  }

  async initializeMap() {
    const apiKey = this.dataset.googleMapsApiKey?.trim();
    if (!apiKey) {
      this.showMapMessage('Add your Google Maps API key in this section’s Theme Editor settings.');
      return;
    }

    const locations = this.cards
      .map((card) => ({ card, position: this.getCardPosition(card) }))
      .filter(({ position }) => position !== null);

    if (!locations.length) {
      this.showMapMessage('Add latitude and longitude to at least one retailer block.');
      return;
    }

    try {
      await this.loadGoogleMaps(apiKey);
      if (!window.google?.maps || !this.canvas) throw new Error('Google Maps did not initialize.');

      const initialPosition = locations[0].position;
      this.map = new google.maps.Map(this.canvas, {
        center: initialPosition,
        zoom: Number(this.dataset.mapZoom) || 11,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
        gestureHandling: 'cooperative',
      });
      this.infoWindow = new google.maps.InfoWindow();

      const bounds = new google.maps.LatLngBounds();
      locations.forEach(({ card, position }) => {
        const marker = new google.maps.Marker({
          map: this.map,
          position,
          title: card.dataset.mapTitle || '',
          icon: this.createMarkerIcon(false),
          optimized: true,
        });

        this.markers.set(card, marker);
        bounds.extend(position);

        card.addEventListener('mouseenter', () => {
          marker.setAnimation(google.maps.Animation.BOUNCE);
        });
        card.addEventListener('mouseleave', () => {
          marker.setAnimation(null);
        });

        marker.addListener('mouseover', () => this.openInfoWindow(card, marker, false));
        marker.addListener('mouseout', () => {
          if (this.persistentInfoCard !== card) this.infoWindow?.close();
        });
        marker.addListener('click', () => {
          this.selectRetailer(card, { openInfo: true, pan: true });
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });

      if (locations.length > 1) {
        this.map.fitBounds(bounds, 48);
      } else {
        this.map.setZoom(Number(this.dataset.mapZoom) || 11);
      }

      this.selectRetailer(this.activeCard || locations[0].card, { openInfo: false, pan: false });
      this.syncVisibleMarkers();
    } catch (error) {
      console.error('[Retailer map]', error);
      this.showMapMessage('The retailer map could not load. Check the API key and its domain restrictions.');
    }
  }

  loadGoogleMaps(apiKey) {
    if (window.google?.maps) return Promise.resolve();
    if (window.whatDHookGoogleMapsPromise) return window.whatDHookGoogleMapsPromise;

    window.whatDHookGoogleMapsPromise = new Promise((resolve, reject) => {
      const callbackName = `whatDHookMapReady_${Date.now()}`;
      const script = document.createElement('script');
      window[callbackName] = () => {
        delete window[callbackName];
        resolve();
      };
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&v=quarterly&callback=${callbackName}`;
      script.async = true;
      script.onerror = () => {
        delete window[callbackName];
        reject(new Error('Unable to load Google Maps JavaScript API.'));
      };
      document.head.append(script);
    });

    return window.whatDHookGoogleMapsPromise;
  }

  getCardPosition(card) {
    const latitude = Number.parseFloat(card.dataset.latitude || '');
    const longitude = Number.parseFloat(card.dataset.longitude || '');
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    return { lat: latitude, lng: longitude };
  }

  createMarkerIcon(isActive) {
    const color = isActive ? '#C0392B' : '#3578A8';
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42">
        <path fill="${color}" stroke="#ffffff" stroke-width="1.5" d="M15 1C7.27 1 1 7.27 1 15c0 10.5 14 25.5 14 25.5S29 25.5 29 15C29 7.27 22.73 1 15 1Z"/>
        <circle cx="15" cy="15" r="5.25" fill="#ffffff"/>
      </svg>`;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(30, 42),
      anchor: new google.maps.Point(15, 42),
    };
  }

  selectRetailer(card, { openInfo = false, pan = false } = {}) {
    if (!card || card.hidden) return;
    this.activeCard = card;

    this.cards.forEach((retailerCard) => {
      const isActive = retailerCard === card;
      retailerCard.classList.toggle('is-active', isActive);
      if (isActive) {
        retailerCard.setAttribute('aria-current', 'true');
      } else {
        retailerCard.removeAttribute('aria-current');
      }

      const marker = this.markers.get(retailerCard);
      if (marker) {
        marker.setIcon(this.createMarkerIcon(isActive));
        marker.setZIndex(isActive ? 1000 : undefined);
      }
    });

    const marker = this.markers.get(card);
    const position = this.getCardPosition(card);
    if (!this.map || !marker || !position) return;

    if (pan) {
      this.map.panTo(position);
      const requestedZoom = Number(this.dataset.mapZoom) || 11;
      if ((this.map.getZoom() || 0) < requestedZoom) this.map.setZoom(requestedZoom);
    }

    if (openInfo) {
      this.persistentInfoCard = card;
      this.openInfoWindow(card, marker, true);
    }
  }

  openInfoWindow(card, marker, persistent) {
    if (!this.infoWindow || !this.map) return;
    if (persistent) this.persistentInfoCard = card;
    this.infoWindow.setContent(this.buildInfoWindowContent(card));
    this.infoWindow.open({ map: this.map, anchor: marker, shouldFocus: false });
  }

  buildInfoWindowContent(card) {
    const title = this.escapeHtml(card.dataset.mapTitle || 'Retailer');
    const address = this.escapeHtml(card.dataset.mapAddress || '');
    const stock = this.escapeHtml(card.dataset.mapStock || '');
    const phone = this.escapeHtml(card.dataset.mapPhone || '');
    const phoneHref = phone.replace(/[^+\d]/g, '');
    const query = encodeURIComponent(card.dataset.mapQuery || `${title}, ${address}`);

    return `
      <div class="retailer-map-info">
        <h3 class="retailer-map-info__title">${title}</h3>
        ${address ? `<p class="retailer-map-info__address">${address}</p>` : ''}
        ${(stock || phone) ? `<p class="retailer-map-info__meta">${stock ? `<span class="retailer-map-info__stock">● ${stock}</span>` : ''}${stock && phone ? ' &nbsp; ' : ''}${phone ? `<a href="tel:${phoneHref}">${phone}</a>` : ''}</p>` : ''}
        <a class="retailer-map-info__directions" href="https://www.google.com/maps/search/?api=1&amp;query=${query}" target="_blank" rel="noopener">Get Directions →</a>
      </div>`;
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  showMapMessage(text) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = false;
  }

  filterRetailers(query) {
    this.filterQuery = this.normalizeSearchText(query);
    let matchCount = 0;

    this.cards.forEach((card) => {
      const isMatch = !this.filterQuery || this.matchesSearch(card, this.filterQuery);
      card.hidden = !isMatch;
      if (isMatch) matchCount += 1;
    });

    const emptyState = this.querySelector('[data-retailer-no-results]');
    if (emptyState) emptyState.hidden = matchCount > 0;

    this.updateResultsHeading(query, matchCount);

    this.infoWindow?.close();
    this.persistentInfoCard = null;
    this.syncVisibleMarkers();

    const firstVisibleCard = Array.from(this.list?.querySelectorAll('[data-retailer-card]') || [])
      .find((card) => !card.hidden);
    if (firstVisibleCard) {
      this.selectRetailer(firstVisibleCard, { openInfo: false, pan: false });
      this.list?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      this.activeCard = null;
      this.cards.forEach((card) => {
        card.classList.remove('is-active');
        card.removeAttribute('aria-current');
      });
    }

    return matchCount;
  }

  updateResultsHeading(query, matchCount) {
    if (!this.resultsHeading) return;

    const trimmedQuery = String(query || '').trim();
    if (!trimmedQuery) {
      this.resultsHeading.textContent = 'Buy What D Hook at Your Local Retailer';
      return;
    }

    const retailerLabel = matchCount === 1 ? 'Retailer' : 'Retailers';
    this.resultsHeading.textContent = `${matchCount} ${retailerLabel} Found for “${trimmedQuery}”`;
  }

  syncVisibleMarkers() {
    if (!this.map || !window.google?.maps) return;

    const bounds = new google.maps.LatLngBounds();
    let visibleMarkerCount = 0;

    this.cards.forEach((card) => {
      const marker = this.markers.get(card);
      if (!marker) return;

      if (card.hidden) {
        marker.setMap(null);
        return;
      }

      marker.setMap(this.map);
      const position = this.getCardPosition(card);
      if (position) bounds.extend(position);
      visibleMarkerCount += 1;
    });

    if (visibleMarkerCount > 1) {
      this.map.fitBounds(bounds, 48);
    } else if (visibleMarkerCount === 1) {
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(Number(this.dataset.mapZoom) || 11);
    }
  }

  normalizeSearchText(value) {
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  matchesSearch(card, normalizedQuery) {
    const searchableText = this.normalizeSearchText(card.dataset.searchText || card.textContent || '');
    const compactSearchableText = searchableText.replaceAll(' ', '');
    const compactQuery = normalizedQuery.replaceAll(' ', '');
    const queryWords = normalizedQuery.split(' ').filter(Boolean);

    return compactSearchableText.includes(compactQuery)
      || queryWords.every((word) => searchableText.includes(word));
  }

  updateVisitorDistances() {
    if (!navigator.geolocation) {
      this.setDistanceUnavailable('Location unavailable');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const visitorLatitude = position.coords.latitude;
        const visitorLongitude = position.coords.longitude;

        this.cards.forEach((card) => {
          const retailerPosition = this.getCardPosition(card);
          const output = card.querySelector('[data-retailer-distance]');

          if (!retailerPosition) {
            if (output) output.textContent = 'Distance unavailable';
            return;
          }

          const miles = this.distanceInMiles(
            visitorLatitude,
            visitorLongitude,
            retailerPosition.lat,
            retailerPosition.lng
          );

          card.dataset.distanceMiles = String(miles);
          if (output) output.textContent = `${this.formatMiles(miles)} mi`;
        });
      },
      () => this.setDistanceUnavailable('Enable location'),
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  }

  setDistanceUnavailable(message) {
    this.cards.forEach((card) => {
      const output = card.querySelector('[data-retailer-distance]');
      if (output) output.textContent = message;
    });
  }

  distanceInMiles(fromLatitude, fromLongitude, toLatitude, toLongitude) {
    const earthRadiusMiles = 3958.7613;
    const toRadians = (degrees) => degrees * (Math.PI / 180);
    const latitudeDelta = toRadians(toLatitude - fromLatitude);
    const longitudeDelta = toRadians(toLongitude - fromLongitude);
    const startLatitude = toRadians(fromLatitude);
    const endLatitude = toRadians(toLatitude);
    const haversine =
      Math.sin(latitudeDelta / 2) ** 2
      + Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDelta / 2) ** 2;

    return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  formatMiles(miles) {
    if (miles < 0.1) return '<0.1';
    return miles.toFixed(1).replace(/\.0$/, '');
  }
}

if (!customElements.get('retailer-map')) {
  customElements.define('retailer-map', RetailerMap);
}
